import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  collection, query, where, orderBy, limit, getDocs, doc, setDoc, getDoc, onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase';
import { ladeSpielerNamen, spielerName } from '../utils/spielerNamen';
import { useAuth } from '../contexts/AuthContext';
import QuickTippButtons from '../components/QuickTippButtons';
import { NFL_TEAMS } from '../data/nflTeams';
import { saisonIdZuName } from '../utils/saison';
import { regularSeasonWochen, wocheName } from '../utils/saisonPhasen';
import { tippKategorie } from '../utils/rankingStatistik';
import './TippspielPage.css';

// Tippseite: eine Woche auf einmal. Liest pro Besuch nur die Spiele der
// gewählten Woche (statt der ganzen Saison), das eigene Tipp-Doc, das eigene
// Punkte-Doc und – falls ein Lieblingsteam gesetzt ist – dessen Spiele.
// Geschrieben wird ausschließlich das eigene Tipp-Doc (docs/DATENMODELL.md).

// Nur als Fallback für Saisons, deren Spiel-Docs noch kein seasonType haben
const LEGACY_PLAYOFF_WOCHEN = [19, 20, 21, 22];

const alsEingabe = (wert) => (wert === undefined || wert === null ? '' : String(wert));

const tippVollstaendig = (tipp) =>
  Boolean(tipp) &&
  tipp.heimScore !== '' && tipp.gastScore !== '' &&
  tipp.heimScore !== undefined && tipp.gastScore !== undefined &&
  tipp.heimScore !== null && tipp.gastScore !== null;

const KATEGORIE_LABEL = {
  exakt: 'Exakt',
  tendenz: 'Tendenz',
  sieger: 'Sieger',
  falsch: 'Daneben'
};

const formatKickoff = (iso) =>
  new Date(iso).toLocaleString('de-DE', {
    weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
  });

function istProBowl(spiel) {
  const h = spiel.heimTeam?.name || '';
  const g = spiel.gastTeam?.name || '';
  return (h === 'AFC' && g === 'NFC') || (h === 'NFC' && g === 'AFC');
}

// Läuft gerade: Kickoff vorbei, noch nicht beendet, nicht verschoben
const istLiveSpiel = (spiel, jetzt) =>
  spiel.completed !== true &&
  spiel.status !== 'STATUS_POSTPONED' &&
  new Date(spiel.datum) <= jetzt;

const hatScore = (wert) => wert !== null && wert !== undefined && wert !== '';

// Zwischenstand oder Endstand vorhanden?
const hatErgebnis = (spiel) => hatScore(spiel.heimTeam?.score) && hatScore(spiel.gastTeam?.score);

function TippspielPage() {
  const { saisonId } = useParams();
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;

  const [saisonConfig, setSaisonConfig] = useState({ deadlinesAktiv: true, gesperrt: false });
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [playoffWochen, setPlayoffWochen] = useState([]);
  const [spieleNachWoche, setSpieleNachWoche] = useState({});
  const [wocheLaedt, setWocheLaedt] = useState(false);
  const [startLaedt, setStartLaedt] = useState(true);

  const [tipps, setTipps] = useState({});             // Eingaben (Strings)
  const [gespeichert, setGespeichert] = useState({}); // Stand in Firestore
  const [punkteDetails, setPunkteDetails] = useState({});

  const [favoritenTeam, setFavoritenTeam] = useState(null);
  const [favoritenSpiele, setFavoritenSpiele] = useState([]);
  const [nurFavoritenTeam, setNurFavoritenTeam] = useState(false);

  // Tipps der anderen – nur für Spiele nach Kickoff, geladen beim ersten Einblenden
  const [zeigeAndere, setZeigeAndere] = useState(false);
  const [andereTipps, setAndereTipps] = useState(null); // [{ userId, name, spiele }]
  const [andereLaden, setAndereLaden] = useState(false);

  const [speichernLaeuft, setSpeichernLaeuft] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  // Uhr im Minutentakt, damit Kickoffs ohne Neuladen als "gestartet" gelten
  const [jetzt, setJetzt] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setJetzt(new Date()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const zeigeToast = useCallback((text, fehler = false) => {
    setToast({ text, fehler });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2500);
  }, []);

  const saisonName = saisonIdZuName(saisonId);

  // ---------- Laden: Startdaten ----------
  useEffect(() => {
    if (!uid) return undefined;
    let abgebrochen = false;

    const ladeStart = async () => {
      setStartLaedt(true);
      setSpieleNachWoche({});
      setSelectedWeek(null);
      setAndereTipps(null);
      setZeigeAndere(false);
      try {
        const spieleCollection = collection(db, 'spiele');
        const [configSnap, tippSnap, punkteSnap, userSnap, naechstesSnap, playoffSnap] = await Promise.all([
          getDoc(doc(db, 'saisonKonfiguration', saisonId)),
          getDoc(doc(db, 'tipps', `${uid}_${saisonId}`)),
          getDoc(doc(db, 'punkte', `${uid}_${saisonId}`)),
          getDoc(doc(db, 'users', uid)),
          getDocs(query(
            spieleCollection,
            where('saison', '==', saisonName),
            where('datum', '>=', new Date().toISOString()),
            orderBy('datum', 'asc'),
            limit(1)
          )),
          getDocs(query(
            spieleCollection,
            where('saison', '==', saisonName),
            where('seasonType', '==', 3)
          ))
        ]);
        if (abgebrochen) return;

        // Ältere Saisons ohne seasonType: Playoffs über die Standard-Wochen suchen
        let playoffSnapDocs = playoffSnap.docs;
        if (playoffSnapDocs.length === 0) {
          const legacySnap = await getDocs(query(
            spieleCollection,
            where('saison', '==', saisonName),
            where('week', 'in', LEGACY_PLAYOFF_WOCHEN)
          ));
          if (abgebrochen) return;
          playoffSnapDocs = legacySnap.docs;
        }

        if (configSnap.exists()) {
          const c = configSnap.data();
          setSaisonConfig({
            deadlinesAktiv: c.deadlinesAktiv !== undefined ? c.deadlinesAktiv : true,
            gesperrt: c.gesperrt || false
          });
        }

        const geladeneTipps = tippSnap.exists() ? (tippSnap.data().spiele || {}) : {};
        const normalisiert = {};
        Object.entries(geladeneTipps).forEach(([id, t]) => {
          normalisiert[id] = { heimScore: alsEingabe(t?.heimScore), gastScore: alsEingabe(t?.gastScore) };
        });
        setTipps(normalisiert);
        setGespeichert(normalisiert);

        setPunkteDetails(punkteSnap.exists() ? (punkteSnap.data().spiele || {}) : {});

        setFavoritenTeam(userSnap.exists() ? (userSnap.data().favoritenTeam || null) : null);

        // Playoff-Wochen, die es schon gibt (Docs entstehen erst nach Import)
        const wochenSet = new Set();
        const playoffDocs = [];
        playoffSnapDocs.forEach(d => {
          const s = { id: d.id, ...d.data() };
          if (!istProBowl(s)) {
            wochenSet.add(s.week);
            playoffDocs.push(s);
          }
        });
        const pw = Array.from(wochenSet).sort((a, b) => a - b);
        setPlayoffWochen(pw);

        // Playoff-Docs direkt in den Wochen-Cache legen
        const cache = {};
        pw.forEach(w => {
          cache[w] = playoffDocs
            .filter(s => s.week === w)
            .sort((a, b) => new Date(a.datum) - new Date(b.datum));
        });
        setSpieleNachWoche(cache);

        // Startwoche: nächstes anstehendes Spiel, sonst letzte bekannte Woche
        let startWoche;
        if (!naechstesSnap.empty) {
          startWoche = naechstesSnap.docs[0].data().week;
        } else {
          startWoche = pw.length > 0 ? pw[pw.length - 1] : regularSeasonWochen(pw);
        }
        setSelectedWeek(startWoche);
      } catch (error) {
        console.error('Fehler beim Laden der Tippseite:', error);
        zeigeToast('Laden fehlgeschlagen', true);
      } finally {
        if (!abgebrochen) setStartLaedt(false);
      }
    };

    ladeStart();
    return () => { abgebrochen = true; };
  }, [uid, saisonId, saisonName, zeigeToast]);

  // ---------- Laden: Spiele der gewählten Woche ----------
  useEffect(() => {
    if (selectedWeek === null || spieleNachWoche[selectedWeek]) return undefined;
    let abgebrochen = false;

    const ladeWoche = async () => {
      setWocheLaedt(true);
      try {
        const snap = await getDocs(query(
          collection(db, 'spiele'),
          where('saison', '==', saisonName),
          where('week', '==', selectedWeek)
        ));
        if (abgebrochen) return;
        const spiele = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(s => !istProBowl(s))
          .sort((a, b) => new Date(a.datum) - new Date(b.datum));
        setSpieleNachWoche(prev => ({ ...prev, [selectedWeek]: spiele }));
      } catch (error) {
        console.error('Fehler beim Laden der Woche:', error);
        zeigeToast('Woche konnte nicht geladen werden', true);
      } finally {
        if (!abgebrochen) setWocheLaedt(false);
      }
    };

    ladeWoche();
    return () => { abgebrochen = true; };
  }, [selectedWeek, saisonName, spieleNachWoche, zeigeToast]);

  // ---------- Laden: Spiele des Lieblingsteams ----------
  useEffect(() => {
    if (!favoritenTeam) {
      setFavoritenSpiele([]);
      return undefined;
    }
    let abgebrochen = false;

    const ladeFavoriten = async () => {
      try {
        const spieleCollection = collection(db, 'spiele');
        const [heim, gast] = await Promise.all([
          getDocs(query(spieleCollection, where('saison', '==', saisonName), where('heimTeam.shortName', '==', favoritenTeam))),
          getDocs(query(spieleCollection, where('saison', '==', saisonName), where('gastTeam.shortName', '==', favoritenTeam)))
        ]);
        if (abgebrochen) return;
        const alle = [...heim.docs, ...gast.docs]
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(s => !istProBowl(s))
          .sort((a, b) => new Date(a.datum) - new Date(b.datum));
        setFavoritenSpiele(alle);
      } catch (error) {
        console.error('Fehler beim Laden der Favoriten-Spiele:', error);
      }
    };

    ladeFavoriten();
    return () => { abgebrochen = true; };
  }, [favoritenTeam, saisonName]);

  // ---------- Live: laufende Spiele der Woche abonnieren ----------
  // Solange mindestens ein Spiel der Woche läuft, lauscht die Seite auf EIN
  // Doc (Live-Aggregat) und das eigene punkte-Doc. Endet das letzte Spiel,
  // werden die Abos beendet; die Spiele im Cache behalten den Endstand.
  const liveIds = (spieleNachWoche[selectedWeek] || [])
    .filter(s => istLiveSpiel(s, jetzt))
    .map(s => s.id)
    .join('|');

  useEffect(() => {
    if (!liveIds) return undefined;
    // Ein Listener auf das Live-Aggregat der Saison (systemConfig/liveRanking_{saison}),
    // das updateNFLScores bei jeder Score-Änderung schreibt – statt je Spiel ein Abo.
    // Es enthält die Zwischenstände aller laufenden Spiele.
    const unsubLive = onSnapshot(doc(db, 'systemConfig', `liveRanking_${saisonId}`), snap => {
      if (!snap.exists()) return;
      const liveSpiele = snap.data().spiele || {};
      if (Object.keys(liveSpiele).length === 0) return;
      // Spiel mit Live-Stand zusammenführen; unverändertes Spiel bleibt dasselbe Objekt
      const mitLive = (s) => {
        const l = liveSpiele[s.id];
        if (!l) return s;
        const gleich = s.status === l.status && s.completed === l.completed &&
          s.heimTeam?.score === l.heimTeam?.score && s.gastTeam?.score === l.gastTeam?.score;
        if (gleich) return s;
        return {
          ...s, status: l.status, completed: l.completed,
          heimTeam: { ...s.heimTeam, score: l.heimTeam?.score },
          gastTeam: { ...s.gastTeam, score: l.gastTeam?.score }
        };
      };
      setSpieleNachWoche(prev => {
        const neu = {};
        let geaendert = false;
        Object.entries(prev).forEach(([week, liste]) => {
          const ersetzt = liste.map(mitLive);
          if (ersetzt.some((s, i) => s !== liste[i])) geaendert = true;
          neu[week] = ersetzt;
        });
        return geaendert ? neu : prev;
      });
    }, error => console.error('Live-Update fehlgeschlagen:', error));

    // Eigene Punkte kommen ~2 Minuten nach dem Endstand von der Function
    const unsubPunkte = uid ? onSnapshot(doc(db, 'punkte', `${uid}_${saisonId}`), snap => {
      if (snap.exists()) setPunkteDetails(snap.data().spiele || {});
    }, () => {}) : null;

    return () => {
      unsubLive();
      if (unsubPunkte) unsubPunkte();
    };
  }, [liveIds, uid, saisonId]);

  // ---------- Laden: Tipps der anderen (beim ersten Einblenden) ----------
  useEffect(() => {
    if (!zeigeAndere || andereTipps !== null || !uid) return undefined;
    let abgebrochen = false;

    const ladeAndere = async () => {
      setAndereLaden(true);
      try {
        const [tippsSnap, namen] = await Promise.all([
          getDocs(query(collection(db, 'tipps'), where('saison', '==', saisonId))),
          ladeSpielerNamen()
        ]);
        if (abgebrochen) return;
        const liste = tippsSnap.docs
          .map(d => ({ userId: d.data().userId, name: spielerName(namen, d.data().userId), spiele: d.data().spiele || {} }))
          .filter(t => t.userId && t.userId !== uid)
          .sort((a, b) => a.name.localeCompare(b.name, 'de'));
        setAndereTipps(liste);
      } catch (error) {
        console.error('Fehler beim Laden der anderen Tipps:', error);
        zeigeToast('Tipps der anderen konnten nicht geladen werden', true);
        setAndereTipps([]);
      } finally {
        if (!abgebrochen) setAndereLaden(false);
      }
    };

    ladeAndere();
    return () => { abgebrochen = true; };
  }, [zeigeAndere, andereTipps, uid, saisonId, zeigeToast]);

  // Tipps der anderen für ein Spiel (nur aufrufen, wenn das Spiel gesperrt ist)
  const andereFuerSpiel = (spiel) => {
    if (!andereTipps) return [];
    return andereTipps
      .map(t => {
        const tipp = t.spiele[spiel.id];
        if (!tippVollstaendig(tipp)) return null;
        return {
          userId: t.userId,
          name: t.name,
          heimScore: parseInt(tipp.heimScore, 10),
          gastScore: parseInt(tipp.gastScore, 10),
          kategorie: hatErgebnis(spiel) ? tippKategorie(tipp, spiel) : null
        };
      })
      .filter(Boolean);
  };

  // ---------- Ableitungen ----------
  const regularWochen = regularSeasonWochen(playoffWochen);
  const verfuegbareWochen = useMemo(() => {
    const regular = Array.from({ length: regularWochen }, (_, i) => i + 1);
    return [...regular, ...playoffWochen];
  }, [regularWochen, playoffWochen]);

  const wochenIndex = verfuegbareWochen.indexOf(selectedWeek);

  const istDeadlineVorbei = useCallback((spiel) => {
    if (!saisonConfig.deadlinesAktiv) return false;
    return new Date() >= new Date(spiel.datum);
  }, [saisonConfig.deadlinesAktiv]);

  const istGesperrt = useCallback((spiel) =>
    saisonConfig.gesperrt || spiel.completed === true || istDeadlineVorbei(spiel),
  [saisonConfig.gesperrt, istDeadlineVorbei]);

  const istFavoritenSpiel = (spiel) =>
    Boolean(favoritenTeam) &&
    (spiel.heimTeam?.shortName === favoritenTeam || spiel.gastTeam?.shortName === favoritenTeam);

  const alleSpieleDerWoche = spieleNachWoche[selectedWeek] || [];
  const spieleDieseWoche = nurFavoritenTeam && favoritenTeam
    ? alleSpieleDerWoche.filter(istFavoritenSpiel)
    : alleSpieleDerWoche;

  const istGeaendert = (spielId) => {
    const t = tipps[spielId] || {};
    const g = gespeichert[spielId] || {};
    return alsEingabe(t.heimScore) !== alsEingabe(g.heimScore) ||
           alsEingabe(t.gastScore) !== alsEingabe(g.gastScore);
  };

  const offeneSpiele = alleSpieleDerWoche.filter(s => !istGesperrt(s));
  const geaenderteSpiele = alleSpieleDerWoche.filter(s =>
    !istGesperrt(s) && istGeaendert(s.id) && tippVollstaendig(tipps[s.id]));
  const unvollstaendigGeaendert = alleSpieleDerWoche.filter(s =>
    !istGesperrt(s) && istGeaendert(s.id) && !tippVollstaendig(tipps[s.id]));
  const getippteAnzahl = alleSpieleDerWoche.filter(s => tippVollstaendig(tipps[s.id])).length;

  const naechstesFavoritenSpiel = useMemo(() => {
    const jetzt = new Date();
    return favoritenSpiele.find(s => !s.completed && new Date(s.datum) > jetzt) || null;
  }, [favoritenSpiele]);

  // ---------- Handler ----------
  const handleTippChange = (spielId, feld, wert) => {
    // nur Ziffern, max. zwei Stellen
    const sauber = wert.replace(/[^0-9]/g, '').slice(0, 2);
    setTipps(prev => ({
      ...prev,
      [spielId]: { ...(prev[spielId] || { heimScore: '', gastScore: '' }), [feld]: sauber }
    }));
  };

  // QuickTippButtons liefert Zahlen oder null – auf Eingabe-Strings bringen
  const setTippsVonQuick = (neu) => {
    const normalisiert = {};
    Object.entries(neu).forEach(([id, t]) => {
      normalisiert[id] = { heimScore: alsEingabe(t?.heimScore), gastScore: alsEingabe(t?.gastScore) };
    });
    setTipps(normalisiert);
  };

  const handleSpeichern = async () => {
    if (!uid || geaenderteSpiele.length === 0) return;
    setSpeichernLaeuft(true);
    try {
      const update = {};
      const jetzt = new Date().toISOString();
      geaenderteSpiele.forEach(spiel => {
        const t = tipps[spiel.id];
        update[spiel.id] = {
          heimScore: parseInt(t.heimScore, 10),
          gastScore: parseInt(t.gastScore, 10),
          gespeichertAm: jetzt
        };
      });

      await setDoc(doc(db, 'tipps', `${uid}_${saisonId}`), {
        userId: uid,
        saison: saisonId,
        spiele: update
      }, { merge: true });

      setGespeichert(prev => {
        const neu = { ...prev };
        Object.entries(update).forEach(([id, t]) => {
          neu[id] = { heimScore: alsEingabe(t.heimScore), gastScore: alsEingabe(t.gastScore) };
        });
        return neu;
      });
      const n = geaenderteSpiele.length;
      zeigeToast(`✓ ${n} ${n === 1 ? 'Tipp' : 'Tipps'} gespeichert`);
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      zeigeToast('Speichern fehlgeschlagen: ' + error.message, true);
    } finally {
      setSpeichernLaeuft(false);
    }
  };

  const springeZuWoche = (richtung) => {
    const ziel = verfuegbareWochen[wochenIndex + richtung];
    if (ziel !== undefined) setSelectedWeek(ziel);
  };

  // ---------- Render ----------
  if (startLaedt || selectedWeek === null) {
    return (
      <div className="tipp-seite">
        <h1>🏈 Saison {saisonName}</h1>
        <div className="spiel-liste__leer">⏳ Lade Spiele…</div>
      </div>
    );
  }

  const kickoffErstes = alleSpieleDerWoche[0]?.datum;

  return (
    <div className="tipp-seite">
      <h1>🏈 Saison {saisonName}</h1>

      {saisonConfig.gesperrt && (
        <div className="tipp-hinweis tipp-hinweis--gesperrt">
          <strong>🔒 Saison abgeschlossen.</strong> Tipps können nicht mehr geändert werden, Ranking und Statistiken bleiben sichtbar.
        </div>
      )}

      {!saisonConfig.deadlinesAktiv && !saisonConfig.gesperrt && (
        <div className="tipp-hinweis tipp-hinweis--test">
          ⚠️ Deadlines sind für diese Saison ausgeschaltet – Tipps gehen auch nach Kickoff.
        </div>
      )}

      {favoritenTeam && naechstesFavoritenSpiel && (
        <FavoritenBanner
          spiel={naechstesFavoritenSpiel}
          team={favoritenTeam}
          nummer={favoritenSpiele.indexOf(naechstesFavoritenSpiel) + 1}
          gesamt={favoritenSpiele.length}
          playoffWochen={playoffWochen}
          onZurWoche={() => { setSelectedWeek(naechstesFavoritenSpiel.week); setNurFavoritenTeam(false); }}
        />
      )}

      <div className="wochen-wahl">
        <div className="wochen-wahl__zeile">
          <button
            type="button"
            className="wochen-wahl__pfeil"
            onClick={() => springeZuWoche(-1)}
            disabled={wochenIndex <= 0}
            aria-label="Vorherige Woche"
          >‹</button>
          <select
            className="wochen-wahl__select"
            value={selectedWeek}
            onChange={(e) => setSelectedWeek(Number(e.target.value))}
            aria-label="Woche wählen"
          >
            {verfuegbareWochen.map(w => (
              <option key={w} value={w}>{wocheName(w, playoffWochen)}</option>
            ))}
          </select>
          <button
            type="button"
            className="wochen-wahl__pfeil"
            onClick={() => springeZuWoche(1)}
            disabled={wochenIndex < 0 || wochenIndex >= verfuegbareWochen.length - 1}
            aria-label="Nächste Woche"
          >›</button>
        </div>
        <div className="wochen-wahl__info">
          {wocheLaedt ? (
            <span>⏳ lädt…</span>
          ) : (
            <>
              <span>{alleSpieleDerWoche.length} Spiele · {getippteAnzahl} getippt</span>
              {kickoffErstes && <span>· Kickoff {formatKickoff(kickoffErstes)}</span>}
            </>
          )}
          {favoritenTeam && (
            <button
              type="button"
              className={`chip${nurFavoritenTeam ? ' ist-aktiv' : ''}`}
              onClick={() => setNurFavoritenTeam(v => !v)}
            >
              ⭐ Nur {favoritenTeam}
            </button>
          )}
          {alleSpieleDerWoche.some(s => istGesperrt(s)) && (
            <button
              type="button"
              className={`chip${zeigeAndere ? ' ist-aktiv' : ''}`}
              onClick={() => setZeigeAndere(v => !v)}
              title="Tipps der anderen Spieler bei Spielen, die schon begonnen haben"
            >
              👥 Tipps der anderen
            </button>
          )}
        </div>
      </div>

      {offeneSpiele.length > 0 && !saisonConfig.gesperrt && (
        <details className="schnell-tipp">
          <summary>⚡ Schnell ausfüllen</summary>
          <QuickTippButtons
            spiele={offeneSpiele}
            tipps={tipps}
            setTipps={setTippsVonQuick}
            onHinweis={zeigeToast}
          />
        </details>
      )}

      {!wocheLaedt && spieleDieseWoche.length === 0 ? (
        <div className="spiel-liste__leer">
          {nurFavoritenTeam
            ? `Kein ${favoritenTeam}-Spiel in ${wocheName(selectedWeek, playoffWochen)}.`
            : `Für ${wocheName(selectedWeek, playoffWochen)} sind noch keine Spiele importiert.`}
        </div>
      ) : (
        <div className="spiel-liste">
          {spieleDieseWoche.map(spiel => (
            <SpielZeile
              key={spiel.id}
              spiel={spiel}
              tipp={tipps[spiel.id] || { heimScore: '', gastScore: '' }}
              gesperrt={istGesperrt(spiel)}
              live={istLiveSpiel(spiel, jetzt)}
              geaendert={istGeaendert(spiel.id)}
              favorit={istFavoritenSpiel(spiel)}
              punkte={punkteDetails[spiel.id]}
              andere={zeigeAndere && istGesperrt(spiel) ? andereFuerSpiel(spiel) : null}
              andereLaden={andereLaden}
              onChange={(feld, wert) => handleTippChange(spiel.id, feld, wert)}
            />
          ))}
        </div>
      )}

      {!saisonConfig.gesperrt && offeneSpiele.length > 0 && (
        <div className="speichern-leiste">
          <span className="speichern-leiste__text">
            {geaenderteSpiele.length > 0
              ? `${geaenderteSpiele.length} geändert`
              : unvollstaendigGeaendert.length > 0
                ? 'Beide Zahlen eintragen'
                : 'Alles gespeichert'}
          </span>
          <button
            type="button"
            className={`speichern-leiste__button${geaenderteSpiele.length > 0 ? ' speichern-leiste__button--geaendert' : ''}`}
            onClick={handleSpeichern}
            disabled={speichernLaeuft || geaenderteSpiele.length === 0}
          >
            {speichernLaeuft ? '⏳ Speichere…' : '💾 Tipps speichern'}
          </button>
        </div>
      )}

      {toast && (
        <div className={`tipp-toast${toast.fehler ? ' tipp-toast--fehler' : ''}`} role="status">
          {toast.text}
        </div>
      )}
    </div>
  );
}

// ---------- Teilkomponenten ----------

function Team({ team, seite }) {
  return (
    <div className={`team team--${seite}`}>
      <img className="team__logo" src={team?.logo} alt="" />
      <div className="team__text">
        <span className="team__kuerzel">{team?.shortName}</span>
        <span className="team__name" title={team?.name}>{team?.name}</span>
      </div>
    </div>
  );
}

function SpielZeile({ spiel, tipp, gesperrt, live, geaendert, favorit, punkte, andere, andereLaden, onChange }) {
  const vollstaendig = tippVollstaendig(tipp);
  const fertig = spiel.completed === true;
  const zwischenstand = !fertig && live && hatErgebnis(spiel);
  const vorlaeufigeKategorie = zwischenstand ? tippKategorie(tipp, spiel) : null;

  let statusKlasse = 'status--offen';
  let statusText = 'offen';
  if (fertig) {
    statusKlasse = 'status--fertig';
    statusText = 'Endstand';
  } else if (zwischenstand) {
    statusKlasse = 'status--live';
    statusText = '🔴 live';
  } else if (gesperrt) {
    statusKlasse = 'status--gesperrt';
    statusText = '🔒 gestartet';
  } else if (geaendert) {
    statusKlasse = 'status--geaendert';
    statusText = vollstaendig ? '● geändert' : '● unvollständig';
  } else if (vollstaendig) {
    statusKlasse = 'status--gespeichert';
    statusText = '✓ gespeichert';
  }

  const klassen = ['spiel-zeile'];
  if (favorit) klassen.push('spiel-zeile--favorit');
  if (geaendert && !gesperrt) klassen.push('spiel-zeile--geaendert');
  if (gesperrt && !fertig && !zwischenstand) klassen.push('spiel-zeile--gesperrt');
  if (zwischenstand) klassen.push('spiel-zeile--live');

  const kategorie = punkte?.kategorie || (punkte ? 'falsch' : null);

  return (
    <div className={klassen.join(' ')}>
      <div className="spiel-zeile__kopf">
        <span>{favorit && '⭐ '}{formatKickoff(spiel.datum)}</span>
        <span className={`spiel-zeile__status ${statusKlasse}`}>{statusText}</span>
      </div>

      <div className="spiel-zeile__mitte">
        <Team team={spiel.gastTeam} seite="gast" />
        <div className="tipp-eingabe">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={alsEingabe(tipp.gastScore)}
            onChange={(e) => onChange('gastScore', e.target.value)}
            disabled={gesperrt}
            aria-label={`Punkte ${spiel.gastTeam?.name}`}
          />
          <span className="tipp-eingabe__trenner">:</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={alsEingabe(tipp.heimScore)}
            onChange={(e) => onChange('heimScore', e.target.value)}
            disabled={gesperrt}
            aria-label={`Punkte ${spiel.heimTeam?.name}`}
          />
        </div>
        <Team team={spiel.heimTeam} seite="heim" />
      </div>

      {zwischenstand && (
        <div className="spiel-zeile__ergebnis spiel-zeile__ergebnis--live">
          <span>
            Zwischenstand <strong>{spiel.gastTeam?.shortName} {parseInt(spiel.gastTeam?.score, 10)} : {parseInt(spiel.heimTeam?.score, 10)} {spiel.heimTeam?.shortName}</strong>
          </span>
          {vorlaeufigeKategorie ? (
            <span className={`punkte-badge punkte-badge--${vorlaeufigeKategorie}`} title="Vorläufig – kann sich bis zum Endstand ändern">
              aktuell {KATEGORIE_LABEL[vorlaeufigeKategorie]}
            </span>
          ) : (
            <span className="punkte-badge punkte-badge--offen">nicht getippt</span>
          )}
        </div>
      )}

      {fertig && (
        <div className="spiel-zeile__ergebnis">
          <span>
            Endstand <strong>{spiel.gastTeam?.shortName} {parseInt(spiel.gastTeam?.score, 10)} : {parseInt(spiel.heimTeam?.score, 10)} {spiel.heimTeam?.shortName}</strong>
          </span>
          {punkte ? (
            <span className={`punkte-badge punkte-badge--${kategorie}`}>
              {KATEGORIE_LABEL[kategorie] || kategorie} · {punkte.punkte ?? 0} P
            </span>
          ) : (
            <span className="punkte-badge punkte-badge--offen">
              {vollstaendig ? 'Punkte folgen' : 'nicht getippt'}
            </span>
          )}
        </div>
      )}
      {andere && (
        <div className="andere-tipps">
          <span className="andere-tipps__titel">👥</span>
          {andere.length === 0 ? (
            <span className="andere-tipps__leer">{andereLaden ? '⏳ lade…' : 'Niemand sonst hat getippt.'}</span>
          ) : andere.map(a => (
            <span
              key={a.userId}
              className={`andere-tipps__eintrag${a.kategorie ? ` andere-tipps__eintrag--${a.kategorie}` : ''}`}
              title={a.kategorie ? KATEGORIE_LABEL[a.kategorie] : undefined}
            >
              <span className="andere-tipps__name">{a.name}</span>
              {a.gastScore}:{a.heimScore}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function FavoritenBanner({ spiel, team, nummer, gesamt, playoffWochen, onZurWoche }) {
  const teamInfo = NFL_TEAMS[team];
  const gegner = spiel.heimTeam?.shortName === team ? spiel.gastTeam : spiel.heimTeam;
  const heim = spiel.heimTeam?.shortName === team;
  const ms = new Date(spiel.datum) - new Date();
  const tage = Math.max(0, Math.floor(ms / 86400000));
  const stunden = Math.max(0, Math.floor((ms % 86400000) / 3600000));

  return (
    <div className="fav-banner">
      <img className="fav-banner__logo" src={teamInfo?.logo} alt="" />
      <div className="fav-banner__text">
        <strong>Nächstes {team}-Spiel: {heim ? 'vs.' : '@'} {gegner?.shortName} · {wocheName(spiel.week, playoffWochen)}</strong>
        {formatKickoff(spiel.datum)} · in {tage > 0 ? `${tage} T ` : ''}{stunden} h · Spiel {nummer}/{gesamt}
      </div>
      <button type="button" className="fav-banner__button" onClick={onZurWoche}>Tippen</button>
    </div>
  );
}

export default TippspielPage;
